import * as React from 'react'
import * as SliderPrimitive from '@radix-ui/react-slider'
import { clsx } from 'clsx'

import { Text } from '../Text'

import styles from './Slider.module.css'

type SliderBaseProps = Omit<
  SliderPrimitive.SliderProps,
  | 'aria-describedby'
  | 'aria-invalid'
  | 'aria-label'
  | 'aria-labelledby'
  | 'aria-valuetext'
  | 'defaultValue'
  | 'inverted'
  | 'minStepsBetweenThumbs'
  | 'onChange'
  | 'onInput'
  | 'onValueChange'
  | 'onValueCommit'
  | 'orientation'
  | 'value'
> & {
  /** Visible label that provides the slider's accessible name. */
  label: React.ReactNode
  /** Current value rendered beside the label. */
  valueLabel?: React.ReactNode
  /** Called whenever pointer or keyboard interaction changes the value. */
  onValueChange?: (value: number) => void
  /** Called when pointer or keyboard interaction finishes changing the value. */
  onValueCommit?: (value: number) => void
  /** Identifies elements that describe the slider. */
  'aria-describedby'?: string
  /** Describes a formatted value for assistive technology. */
  'aria-valuetext'?: string
  /** Indicates that the current value is invalid. */
  'aria-invalid'?: React.AriaAttributes['aria-invalid']
}

type SliderProps = SliderBaseProps &
  (
    | {
        /** Initial value when the slider is uncontrolled. */
        defaultValue?: number
        value?: never
        origin?: never
      }
    | {
        defaultValue?: never
        /** Current value when the slider is controlled. */
        value: number
        /** Optional value from which the active range extends in either direction. */
        origin?: number
      }
  )

interface SliderRangeStyle extends React.CSSProperties {
  '--slider-range-end': `${number}%`
  '--slider-range-start': `${number}%`
}

const callWithSingleValue = (
  callback: ((value: number) => void) | undefined,
  values: number[],
): void => {
  const [value] = values
  if (value !== undefined) callback?.(value)
}

const getValuePosition = (value: number, min: number, max: number): number => {
  if (max <= min) return 0

  const boundedValue = Math.min(Math.max(value, min), max)
  return ((boundedValue - min) / (max - min)) * 100
}

const getOriginRangeStyle = (
  value: number,
  origin: number,
  min: number,
  max: number,
): SliderRangeStyle => {
  const valuePosition = getValuePosition(value, min, max)
  const originPosition = getValuePosition(origin, min, max)

  return {
    '--slider-range-start': `${Math.min(valuePosition, originPosition)}%`,
    '--slider-range-end': `${100 - Math.max(valuePosition, originPosition)}%`,
  }
}

/**
 * Selects one numeric value from a bounded range using pointer and keyboard
 * interaction.
 */
const Slider = React.forwardRef<HTMLSpanElement, SliderProps>(
  (
    {
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
      'aria-valuetext': ariaValueText,
      className,
      defaultValue,
      disabled,
      label,
      max = 100,
      min = 0,
      onValueChange,
      onValueCommit,
      origin,
      style,
      value,
      valueLabel,
      ...props
    }: SliderProps,
    forwardedRef,
  ) => {
    const labelId = React.useId()
    const originRangeStyle =
      origin === undefined || value === undefined
        ? undefined
        : getOriginRangeStyle(value, origin, min, max)

    return (
      <div
        className={clsx(styles.root, className)}
        data-disabled={disabled ? '' : undefined}
        style={style}
      >
        <div className={styles.header}>
          <Text id={labelId} className={styles.label} size="sm" weight="medium">
            {label}
          </Text>
          {valueLabel !== undefined ? (
            <Text
              className={styles.value}
              size="sm"
              color="subtle"
              aria-hidden="true"
            >
              {valueLabel}
            </Text>
          ) : null}
        </div>
        <SliderPrimitive.Root
          {...props}
          ref={forwardedRef}
          className={styles.control}
          value={value === undefined ? undefined : [value]}
          defaultValue={defaultValue === undefined ? undefined : [defaultValue]}
          disabled={disabled}
          min={min}
          max={max}
          onValueChange={(values) => callWithSingleValue(onValueChange, values)}
          onValueCommit={(values) => callWithSingleValue(onValueCommit, values)}
        >
          <SliderPrimitive.Track className={styles.track}>
            {originRangeStyle ? (
              <span className={styles.originRange} style={originRangeStyle} />
            ) : (
              <SliderPrimitive.Range className={styles.range} />
            )}
          </SliderPrimitive.Track>
          <SliderPrimitive.Thumb
            className={styles.thumb}
            aria-labelledby={labelId}
            aria-describedby={ariaDescribedBy}
            aria-disabled={disabled ? true : undefined}
            aria-valuetext={ariaValueText}
            aria-invalid={ariaInvalid}
          />
        </SliderPrimitive.Root>
      </div>
    )
  },
)

Slider.displayName = 'Slider'

export { Slider }
export type { SliderProps }
